import { FiUsers } from 'react-icons/fi';

export const discussionEssaysData = {
  courseId: 'discussion-essays',
  courseType: 'writing-course',
  title: 'Task 2 Discussion Essays',
  icon: FiUsers,
  overview: {
    impact: 'Present balanced arguments and express your opinion diplomatically',
    focus: 'Discuss both views structure, hedging language, fair representation of opposing sides',
    uses: 'IELTS Writing Task 2 — "Discuss both views and give your opinion" questions',
    duration: '25-30 minutes'
  },
  sections: [
    {
      title: 'Understanding the Discussion Essay Format',
      content: `<p>Discussion essays require you to <strong>present both sides of a debate fairly</strong> before giving your own opinion. This differs fundamentally from opinion essays, where you argue for one side throughout.</p>
<p><strong>Standard structure (4-5 paragraphs):</strong></p>
<ul>
  <li><strong>Introduction:</strong> Paraphrase the topic, acknowledge the debate exists, state which side you lean towards.</li>
  <li><strong>Body 1 (View A):</strong> Present the first viewpoint with supporting reasons and evidence.</li>
  <li><strong>Body 2 (View B):</strong> Present the opposing viewpoint with its own supporting reasons.</li>
  <li><strong>Body 3 / Conclusion:</strong> Give your own opinion, explaining why you favour one side (or propose a nuanced middle ground).</li>
</ul>
<p><strong>Key difference from opinion essays:</strong> In a discussion essay, you must give <strong>genuine consideration</strong> to both sides. An examiner can tell if you are dismissing one view to push your own. Balance is essential — even if you ultimately favour one perspective.</p>
<p><strong>Common prompt wordings:</strong></p>
<ul>
  <li>"Discuss both views and give your opinion."</li>
  <li>"Some people think X while others believe Y. Discuss both sides and give your own view."</li>
</ul>`,
      points: [
        'Never ignore one side — underdeveloped View A or View B will cost you marks in Task Achievement.',
        'Your opinion can appear in the introduction (briefly), but must be clearly developed by the end.',
        'Aim for roughly equal paragraph length for View A and View B to show balanced treatment.',
        'You CAN agree with one side more strongly — discussion does not mean you must be neutral.'
      ]
    },
    {
      title: 'Presenting View A Effectively',
      content: `<p>Body Paragraph 1 typically presents View A — the first perspective mentioned in the prompt. Even if you disagree with this view, present it <strong>fairly and convincingly</strong>.</p>
<p><strong>Structure for View A paragraph:</strong></p>
<ol>
  <li><strong>Topic sentence:</strong> Introduce View A using reporting language.<br/>
  <em>"Proponents of stricter internet regulation argue that it is essential for protecting vulnerable users."</em></li>
  <li><strong>Reasoning:</strong> Explain WHY people hold this view.<br/>
  <em>"Their primary concern is that unregulated platforms expose children to harmful content and enable cyberbullying."</em></li>
  <li><strong>Evidence / Example:</strong> Support with a specific illustration.<br/>
  <em>"For instance, a 2023 UNICEF report found that 40% of teenagers had experienced online harassment, prompting calls for platform accountability."</em></li>
  <li><strong>Closing link:</strong> Acknowledge the validity without fully endorsing it.<br/>
  <em>"From this perspective, government intervention appears to be a reasonable safeguard."</em></li>
</ol>
<p><strong>Useful reporting language for View A:</strong></p>
<ul>
  <li>"Those who support X contend that…"</li>
  <li>"Advocates of this approach maintain that…"</li>
  <li>"One school of thought holds that…"</li>
  <li>"It is often argued that…"</li>
</ul>`,
      points: [
        'Use third-person reporting language (they argue, proponents claim) — never say "I think" in View A.',
        'Provide at least one specific reason and one piece of evidence for the view.',
        'End the paragraph with a neutral or slightly concessive closing — do not refute View A here.',
        'Strong View A presentation shows the examiner that you understand the complexity of the issue.'
      ]
    },
    {
      title: 'Presenting View B and Your Opinion',
      content: `<p>Body Paragraph 2 presents the opposing perspective (View B). In a discussion essay, this is also where you can begin signalling your own position — but <strong>subtly</strong>, through the strength of your argument rather than explicit statements.</p>
<p><strong>Structure for View B paragraph:</strong></p>
<ol>
  <li><strong>Transition + Topic sentence:</strong> Pivot to the opposing view.<br/>
  <em>"On the other hand, opponents of internet regulation maintain that it poses a threat to freedom of expression."</em></li>
  <li><strong>Reasoning:</strong> Explain the logic behind View B.<br/>
  <em>"They argue that censorship, even when well-intentioned, inevitably expands and suppresses legitimate discourse."</em></li>
  <li><strong>Evidence:</strong> Provide supporting detail.<br/>
  <em>"History has shown that regulatory frameworks initially targeting harmful content often end up restricting political speech, as seen in several authoritarian states."</em></li>
  <li><strong>Your opinion (subtle):</strong> Signal your position if appropriate.<br/>
  <em>"While both perspectives have merit, the risk to democratic freedoms is arguably the more pressing concern."</em></li>
</ol>
<p><strong>Stating your opinion:</strong> You have three options:</p>
<ul>
  <li><strong>Agree with View A:</strong> Present View B, then explain why View A is more convincing.</li>
  <li><strong>Agree with View B:</strong> Make View B your longer, more detailed paragraph.</li>
  <li><strong>Balanced position:</strong> Argue that both have validity and suggest a synthesis.</li>
</ul>`,
      points: [
        'The paragraph you agree with should be slightly stronger (better evidence, more development).',
        'Express your opinion in the conclusion clearly — do not leave the examiner guessing.',
        'Avoid fence-sitting without justification: "Both sides are correct" scores poorly unless you explain a synthesis.',
        'Use comparative language: "more compelling," "outweighs," "the stronger argument" to signal your preference.'
      ]
    },
    {
      title: 'Hedging and Diplomatic Language',
      content: `<p>Hedging softens absolute claims, making your writing sound <strong>measured, academic, and intellectually honest</strong>. It is crucial in discussion essays where you must handle multiple perspectives respectfully.</p>
<p><strong>Hedging categories:</strong></p>
<ul>
  <li><strong>Modal verbs:</strong> <em>"Technology <u>may</u> contribute to isolation." / "This <u>could</u> lead to negative outcomes."</em></li>
  <li><strong>Adverbs of frequency/likelihood:</strong> <em>"<u>Generally</u>, urban areas offer better services." / "This is <u>arguably</u> the most effective solution."</em></li>
  <li><strong>Impersonal constructions:</strong> <em>"<u>It could be argued that</u>…" / "<u>There is evidence to suggest that</u>…"</em></li>
  <li><strong>Tentative verbs:</strong> <em>"The data <u>suggests</u>…" / "This <u>appears to</u> support…" / "Research <u>indicates</u>…"</em></li>
  <li><strong>Qualifying phrases:</strong> <em>"<u>To a certain extent</u>,…" / "<u>In most cases</u>,…" / "<u>Under certain conditions</u>,…"</em></li>
</ul>
<p><strong>Without hedging:</strong> <em>"Social media causes depression in teenagers."</em> (too absolute)<br/>
<strong>With hedging:</strong> <em>"Research suggests that excessive social media use may contribute to depressive symptoms among teenagers."</em></p>
<p><strong>Diplomatic opinion statements:</strong></p>
<ul>
  <li>"On balance, I am inclined to believe that…"</li>
  <li>"While I acknowledge the merits of both arguments, I find the case for X more persuasive."</li>
  <li>"Having considered both perspectives, I would argue that…"</li>
</ul>`,
      points: [
        'Hedge claims you cannot prove absolutely — "X causes Y" vs "X may contribute to Y."',
        'Use hedging most heavily when presenting the view you disagree with — it shows respect.',
        'In your conclusion, you CAN be more direct about your opinion — some hedging is fine, but do not be vague.',
        'Over-hedging ("It might possibly perhaps be the case that…") weakens your writing — one hedge per claim is enough.'
      ]
    }
  ],
  exercises: [
    {
      title: 'Identify Discussion vs Opinion Essay Prompts',
      type: 'select',
      instructions: 'Determine whether each IELTS Task 2 prompt requires a discussion essay or an opinion essay.',
      questions: [
        {
          text: '"Some people believe that children should begin formal education at age four, while others think seven is a more appropriate starting age. Discuss both views and give your opinion."',
          options: [
            'Opinion essay — because you give your opinion at the end',
            'Discussion essay — because you must present BOTH views before giving your opinion',
            'Problem-solution essay'
          ],
          answer: 'Discussion essay — because you must present BOTH views before giving your opinion',
          explanation: 'The phrase "Discuss both views and give your opinion" requires you to explore both perspectives fairly in separate body paragraphs. Simply arguing for one side without adequately presenting the other would lose marks.'
        },
        {
          text: '"Some people think that the best way to reduce crime is through education. To what extent do you agree or disagree?"',
          options: [
            'Discussion essay — two views are implied',
            'Opinion essay — you are asked for YOUR position on one claim',
            'Cause-and-effect essay'
          ],
          answer: 'Opinion essay — you are asked for YOUR position on one claim',
          explanation: '"To what extent do you agree or disagree?" asks for your opinion on a single proposition. You may address a counter-argument, but you do not need to present both sides equally.'
        },
        {
          text: '"In many countries, people are living longer. Some see this as a positive development, while others argue it creates problems for society. Discuss both perspectives and give your own opinion."',
          options: [
            'Discussion essay',
            'Opinion essay',
            'Advantage-disadvantage essay'
          ],
          answer: 'Discussion essay',
          explanation: '"Discuss both perspectives and give your own opinion" is a variant of the classic discussion essay prompt. You must dedicate paragraphs to both the positive and negative perspectives of increased life expectancy.'
        },
        {
          text: 'What is the KEY structural difference between discussion and opinion essays?',
          options: [
            'Discussion essays are longer than opinion essays',
            'In discussion essays, you must give equal and fair treatment to BOTH sides in separate paragraphs before stating your preference',
            'Opinion essays do not allow you to mention counter-arguments',
            'Discussion essays should not include your personal opinion'
          ],
          answer: 'In discussion essays, you must give equal and fair treatment to BOTH sides in separate paragraphs before stating your preference',
          explanation: 'The defining feature of a discussion essay is balanced presentation of both perspectives. Opinion essays argue for one side throughout, though they may include a counter-argument. Discussion essays require dedicated paragraphs for each viewpoint.'
        }
      ]
    },
    {
      title: 'Match Views to Supporting Arguments',
      type: 'select',
      instructions: 'For the topic "Some believe university should be free, while others think students should pay tuition fees," match each argument to the correct view.',
      questions: [
        {
          text: '"Free education reduces social inequality by ensuring talented students from low-income families can access higher learning." — This supports:',
          options: [
            'View A: University should be free',
            'View B: Students should pay tuition fees'
          ],
          answer: 'View A: University should be free',
          explanation: 'The argument focuses on equality of access regardless of income — a core justification for free education. It would appear in the View A body paragraph.'
        },
        {
          text: '"When students invest their own money, they tend to value their education more and are more motivated to succeed." — This supports:',
          options: [
            'View A: University should be free',
            'View B: Students should pay tuition fees'
          ],
          answer: 'View B: Students should pay tuition fees',
          explanation: 'This argument is about personal investment driving motivation — a common justification for charging tuition. It would appear in the View B paragraph.'
        },
        {
          text: '"Free university education would be funded by taxpayers, including those who never attended university, which raises fairness concerns." — This supports:',
          options: [
            'View A: University should be free',
            'View B: Students should pay tuition fees'
          ],
          answer: 'View B: Students should pay tuition fees',
          explanation: 'The fairness-to-taxpayers argument is used against free education. It questions why non-graduates should subsidise graduates who will likely earn higher salaries.'
        },
        {
          text: '"Countries like Germany and Norway, which offer free university education, consistently rank among the highest in innovation and economic productivity." — This supports:',
          options: [
            'View A: University should be free',
            'View B: Students should pay tuition fees'
          ],
          answer: 'View A: University should be free',
          explanation: 'This uses real-world evidence (Germany, Norway) to show that free education correlates with positive national outcomes, directly supporting the case for free university.'
        }
      ]
    },
    {
      title: 'Complete Hedging Sentences',
      type: 'fill-in',
      instructions: 'Fill in the blank with an appropriate hedging word or phrase to make each statement more academic and diplomatic.',
      questions: [
        {
          text: '"Social media ___ depression in young people." (hedge the word "causes")',
          answer: 'may contribute to',
          explanation: '"May contribute to" is a double hedge (modal verb + weaker verb) that appropriately softens an unproven causal claim. In academic writing, we rarely claim direct causation without strong evidence.'
        },
        {
          text: '"___, traditional classroom teaching is more effective than online learning." (add a qualifying phrase at the start)',
          answer: 'In most cases',
          explanation: '"In most cases" limits the scope of the claim, acknowledging exceptions. Other acceptable hedges: "Generally speaking," "In many contexts," or "For the majority of students."'
        },
        {
          text: '"The data ___ that urban residents have better access to healthcare." (use a tentative reporting verb)',
          answer: 'suggests',
          explanation: '"Suggests" is weaker than "proves" or "shows," making the claim appropriately tentative. In IELTS writing, "suggest," "indicate," and "imply" are safer than "prove" or "demonstrate" unless the evidence is conclusive.'
        },
        {
          text: '"Having considered both perspectives, I ___ that the benefits of globalisation outweigh the drawbacks." (use a diplomatic opinion phrase)',
          answer: 'would argue',
          explanation: '"Would argue" is a hedged opinion marker — more diplomatic than "believe" or "think" and more assertive than "might suggest." It strikes the right balance for a discussion essay conclusion.'
        }
      ]
    },
    {
      title: 'Order a Discussion Essay Outline',
      type: 'select',
      instructions: 'Select the correct order for the components of a discussion essay.',
      questions: [
        {
          text: 'Which element should come FIRST in a discussion essay introduction?',
          options: [
            'Your opinion on the topic',
            'A paraphrase of the topic that establishes the debate',
            'Evidence supporting View A',
            'A counter-argument'
          ],
          answer: 'A paraphrase of the topic that establishes the debate',
          explanation: 'The introduction opens by paraphrasing the prompt and establishing that a debate exists. Your opinion can appear briefly at the end of the introduction but should not lead it.'
        },
        {
          text: 'After presenting View A in Body Paragraph 1, what should Body Paragraph 2 begin with?',
          options: [
            'Your personal opinion immediately',
            'A transition that acknowledges View A and pivots to View B',
            'A repetition of View A for emphasis',
            'The conclusion'
          ],
          answer: 'A transition that acknowledges View A and pivots to View B',
          explanation: 'Body 2 should open with a transition like "On the other hand," "Conversely," or "Those who hold the opposing view argue that…" This acknowledges the previous paragraph and smoothly introduces the contrasting perspective.'
        },
        {
          text: 'Where should your personal opinion be most clearly stated?',
          options: [
            'Only in the introduction',
            'Equally throughout every paragraph',
            'In the conclusion — after both views have been presented and considered',
            'It should never be stated in a discussion essay'
          ],
          answer: 'In the conclusion — after both views have been presented and considered',
          explanation: 'Your opinion should be clearest in the conclusion. You may hint at it subtly in the introduction and through the relative strength of your arguments, but the conclusion is where you explicitly state and justify your position.'
        },
        {
          text: 'Which conclusion is best for a discussion essay about whether governments or individuals should address climate change?',
          options: [
            '"In conclusion, both sides have valid points."',
            '"In conclusion, I believe governments are entirely responsible for climate change."',
            '"In conclusion, while individual action is valuable, I believe that systemic governmental policy is essential to achieve the scale of change required, as individual efforts alone cannot address global supply chains and industrial emissions."',
            '"In conclusion, there is no right answer to this question."'
          ],
          answer: '"In conclusion, while individual action is valuable, I believe that systemic governmental policy is essential to achieve the scale of change required, as individual efforts alone cannot address global supply chains and industrial emissions."',
          explanation: 'This conclusion acknowledges both sides (individual action is "valuable"), clearly states a preference (governmental policy), and provides specific reasoning (scale, supply chains). Options A and D are fence-sitting; B ignores the other view.'
        }
      ]
    },
    {
      title: 'Write a "Both Views" Paragraph',
      type: 'writing',
      instructions: 'Practise writing paragraphs that present a viewpoint fairly and diplomatically.',
      questions: [
        {
          text: 'Write a View A paragraph for: "Some people believe that zoos play a vital role in animal conservation, while others argue they are unethical. Discuss both views." Present the PRO-ZOO view.',
          example: 'Proponents of zoos argue that they serve a crucial role in preserving endangered species. Through carefully managed breeding programmes, zoos have brought animals such as the Arabian oryx and the California condor back from the brink of extinction. Furthermore, zoos fund field conservation projects and raise public awareness about biodiversity loss. From this standpoint, the controlled environment of a modern zoo represents a necessary compromise to ensure the survival of species that would otherwise disappear from the wild entirely.',
          tip: 'Use third-person reporting language ("Proponents argue…"), include one specific example, and end with a neutral closing that does not refute the view — save your rebuttal for later.'
        },
        {
          text: 'Write a diplomatic conclusion for: "Some think social media improves communication, while others believe it damages relationships." State your opinion favouring the negative view.',
          example: 'In conclusion, while social media has undeniably expanded our ability to connect across distances, I am inclined to believe that its impact on the quality of human relationships is largely detrimental. The superficiality of online interactions, combined with growing evidence linking heavy usage to loneliness and anxiety, suggests that the convenience of digital communication comes at a significant interpersonal cost. A more balanced approach to social media use, rather than uncritical adoption, would better serve society\'s relational wellbeing.',
          tip: 'Acknowledge the other side briefly ("while social media has…"), state your opinion clearly ("I am inclined to believe"), give specific reasons, and end with a forward-looking suggestion.'
        },
        {
          text: 'Write an introduction for: "Some people think that competitive sports teach children important life skills, while others believe they place too much pressure on young people. Discuss both views and give your opinion."',
          example: 'The role of competitive sports in childhood development has long been debated. While some maintain that athletic competition builds resilience, teamwork, and discipline, others express concern that the pressure to win can harm children\'s mental health and self-esteem. This essay will examine both perspectives before arguing that, with appropriate adult guidance, the benefits of competitive sports generally outweigh the risks.',
          tip: 'Paraphrase the topic (do not copy the prompt), briefly acknowledge both sides using reporting language, and signal your position in the final sentence without fully developing it.'
        }
      ]
    }
  ],
  tips: [
    'Read the prompt twice — distinguish between "discuss both views" (discussion) and "to what extent do you agree" (opinion) before planning.',
    'Spend 5 minutes planning which arguments go under View A and View B — misplacing an argument confuses the examiner.',
    'Your View A and View B paragraphs should each be 80-100 words to show genuine engagement with both sides.',
    'Use hedging language when presenting the view you disagree with — it shows respect and academic maturity.'
  ],
  dailyPractice: [
    'Pick a controversial topic and write two paragraphs: one arguing FOR and one AGAINST, each with a reason and an example.',
    'Practise writing 3 different hedged versions of the same strong claim (e.g., "Technology destroys jobs").',
    'Read an editorial and identify which sentences present View A, View B, and the author\'s opinion.',
    'Time yourself writing a full discussion essay (introduction + 2 body paragraphs + conclusion) in 40 minutes.'
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Task 2 Discussion Essays course for IELTS Writing. Covers the "Discuss both views and give your opinion" essay format, including balanced presentation of opposing viewpoints, hedging and diplomatic language, reporting verbs, and structuring conclusions that clearly state a preference. Suitable for Band 6-9 candidates who need to distinguish discussion essays from opinion essays.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...discussionEssaysData,
      title: discussionEssaysData.title + ' (Foundations)',
      overview: { ...discussionEssaysData.overview, duration: '15-20 minutes' },
      sections: discussionEssaysData.sections.slice(0, Math.max(2, Math.ceil(discussionEssaysData.sections.length / 2))),
      exercises: discussionEssaysData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...discussionEssaysData,
      title: discussionEssaysData.title + ' (Advanced)',
      overview: { ...discussionEssaysData.overview, duration: '30-35 minutes' },
      sections: discussionEssaysData.sections,
      exercises: discussionEssaysData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(discussionEssaysData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return discussionEssaysData;
}
